import React, { useEffect, useRef, useState } from 'react';
import TopNavPanel, { pressedOutside } from './TopNavPanel';
import { Check, ChevronDown, Loader2 } from 'lucide-react';
import { api } from '../api';
import { useAuth } from '../AuthContext';
import { toast } from '../../hooks/use-toast';

// Workspace switcher (ticket 59, ADR-0003). Lists the caller's active seats
// and switches the active tenant with one POST. Hidden when the user has
// exactly one membership — nothing to switch to.
//
// After a switch the app reloads at /portal: every page caches tenant-keyed
// data on mount (dashboard cover photo, pipeline stages, branding…), and a
// hard navigation is the one invalidation that cannot miss any of them.
//
// variant="drawer" renders the same list inline for the mobile nav drawer; the
// topnav dropdown is md+ only, so phones switch workspace from the drawer.
export default function WorkspaceSwitcher({ variant = 'topnav' }) {
  const { user, switchTenant } = useAuth();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const ref = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    Promise.resolve(api.get('/tenants/memberships'))
      .then(({ data }) => { if (!cancelled) setItems(data.items || []); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (pressedOutside(e, [ref, panelRef])) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (!items || items.length < 2) return null;
  const active = items.find((i) => i.is_active) || items[0];

  const pick = async (item) => {
    if (item.is_active || busy) { setOpen(false); return; }
    setBusy(item.tenant_id);
    try {
      await switchTenant(item.tenant_id);
      window.location.assign('/portal');
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast({ title: 'Could not switch workspace', description: (typeof d === 'object' ? d?.message : d) || 'Please try again.' });
      setBusy(null);
    }
  };

  const renderOption = (item, testIdPrefix) => (
    <button
      key={item.tenant_id}
      type="button"
      onClick={() => pick(item)}
      disabled={!!busy}
      data-testid={`${testIdPrefix}-${item.tenant_id}`}
      className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-white/85 hover:bg-white/5 disabled:opacity-60"
    >
      <span className="w-4 flex justify-center">
        {busy === item.tenant_id ? <Loader2 size={12} className="animate-spin" /> : item.is_active ? <Check size={12} style={{ color: '#A6E56C' }} /> : null}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block truncate">{item.name}</span>
        <span className="block text-[10px] text-white/50">
          {item.kind === 'personal' ? 'Personal workspace' : (item.role || '').replace(/_/g, ' ')}
        </span>
      </span>
    </button>
  );

  if (variant === 'drawer') {
    return (
      <div className="md:hidden border-t border-white/10 px-2 pt-2" data-testid="workspace-switcher-drawer">
        <div className="px-3 pb-1 text-[10px] uppercase tracking-wider text-white/40">Workspace</div>
        {items.map((item) => renderOption(item, 'workspace-drawer-option'))}
      </div>
    );
  }

  return (
    <div className="relative shrink-0" ref={ref} data-testid="workspace-switcher">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="workspace-switcher-toggle"
        className="hidden md:flex items-center gap-1.5 max-w-[220px] px-2.5 py-1.5 rounded-md text-xs font-medium text-white/85 hover:bg-white/10 transition"
        style={{ border: '1px solid rgba(255,255,255,0.12)' }}
        title="Switch workspace"
      >
        <span className="truncate">{active.name}</span>
        {active.kind === 'personal' && <span className="text-[9px] uppercase tracking-wider text-white/50">personal</span>}
        <ChevronDown size={12} className="opacity-70" />
      </button>
      {open && (
        <TopNavPanel ref={panelRef} anchorRef={ref} onDismiss={() => setOpen(false)} align="left" className="w-72 rounded-lg border shadow-2xl py-1.5" style={{ background: '#070B5F', borderColor: 'rgba(107,168,46,0.25)' }} data-testid="workspace-switcher-menu">
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-white/40">Workspaces</div>
          {items.map((item) => renderOption(item, 'workspace-option'))}
        </TopNavPanel>
      )}
    </div>
  );
}
